import { quatFromAxisAngle, quatMultiply } from '../engine/quat'
import { ObjectRuntimeState, Quat, Vec3 } from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import {
  FAIL_GAMEOVER_SFX,
  GOAL_SUCCESS_SFX,
  SampleSfx,
  TARGET_HIT_SFX,
} from './missionSfx'
import { BehaviorHandler, HudStyle, MissionBehaviors } from './types'

/**
 * RJS04（やきゅうゲーム）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS04.rbxl）のギミックを移植する:
 * - ②バット/BatScript: ③ボールがバットに触れたら（Hit フラグが false の
 *   ときだけ）Hit = true。実機はあわせてボールの AssemblyLinearVelocity を
 *   5 倍にする（下記の未対応を参照）。
 * - ServerStorage/CollisionScript（CloneScript が Collisions/Collision ×3 へ
 *   複製）: ③ボールがファールゾーン（透明な判定箱）に触れたら一度だけ判定。
 *   バットに当たった後なら「ファール」、当たっていなければ「ストライク」+
 *   OutSound。
 * - ServerStorage/ScoreScript（CloneScript が Score/* と ④ホームランゾーン へ
 *   複製）: ③ボールがスコアボードに触れたら一度だけ部位名で判定。
 *   Out→「アウト」+ OutSound / Hit→「ヒット！」+ HitSound /
 *   2Base→「ツーベース！！」+ HitSound / HomeRun・④ホームランゾーン→
 *   「ホームラン！！！」+ HomeRunSound。
 * - 判定は全体で 1 球 1 回（ReplicatedStorage.Judge。どのゾーンかを問わず
 *   最初の接触で確定し、以降は何にも反応しない）。
 * - StarterGui/ScreenGui/GUILocalScript: 判定結果を画面上部の帯に表示する。
 *   ツーベースは背景が水色、ホームランは背景が黄色 + 赤枠 5px になる
 *   （rbxl の TextLabel 実測値）。
 *
 * プレビューでの近似・意図的な差異:
 * - ボールとバット・判定ゾーンの接触は、Lua の Touched の代わりに
 *   球 vs 回転箱（OBB）の毎フレーム監視で判定する（オブジェクト同士の
 *   Touched はランタイムの接触イベントに乗らないため）。ファールゾーンは
 *   Y 軸 45 度回転の大きな箱のため、AABB 近似では公平打球まで誤判定する。
 *   ボールが 1 フレームで判定板（厚さ 2 studs）を跳び越えるほど速い場合は
 *   取りこぼし得る（rjf03/rjf12 と同じ割り切り）。
 * - 同一フレームで複数ゾーンに同時に触れた場合はシーン順（World の登録順）で
 *   先のゾーンが勝つ（実機はどの Touched が先に発火するか物理依存）。
 * - HitSound / OutSound / HomeRunSound（実機の音源はアセット取得不可）は
 *   プロダクト提供の実音源で鳴らす（ヒット・ツーベース=マト命中音、
 *   ホームラン=成功音、アウト・ファール・ストライク=失敗音）。
 * - BGM はシーン JSON の bgm が再生する（ビヘイビア対象外）。実機の
 *   'Baseball Charge' はプロダクト指定の Sunrise（bgm-overrides.json）へ
 *   差し替え済み。
 * - BatScript のボール加速（AssemblyLinearVelocity を 5 倍）は、ボールが動的剛体
 *   （学習者が「うごくようにする」で解除済み）のとき world.setObjectVelocity で
 *   再現する。Roblox 実機では動いているバットとの物理衝突でボールに反発速度が
 *   乗ったうえで Touched が発火し得るが、プレビューの固定 body の姿勢更新は
 *   その速度を動的ボールへ伝えない。そこで 5 倍後の速度が打球方向へ向いて
 *   いない場合だけ、フィールド側への最低速度と上向き速度を補う。Anchored の
 *   ままで速度が無いときは何もしない。
 */

/** ボール・バットのオブジェクト名（学習者のプログラム対象でもある）。 */
const BALL_NAME = '③ボール'
const BAT_NAME = '②バット'

/** バット接触時のボール速度の倍率（Lua: hit.AssemblyLinearVelocity = 5 * ...）。 */
const BAT_HIT_VELOCITY_SCALE = 5

/**
 * プレビューの物理では、回転/移動中の固定バットが動的ボールへ反発速度を
 * 伝えられないため、BatScript の 5 倍処理に重ねる最低打球速度。
 */
const BAT_HIT_MIN_FIELD_SPEED = 160
const BAT_HIT_MIN_UPWARD_SPEED = 60
const DEFAULT_FIELD_DIRECTION_Z = -1
const EPSILON = 1e-6

/**
 * 判定 GUI の基本デザイン（StarterGui/ScreenGui/TextLabel の実測値:
 * 白背景・黒文字・画面幅いっぱい・上端から 50px）。
 */
const JUDGE_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#000000',
  wide: true,
  top: 50,
}

/** ツーベースの背景色（Lua: Color3.new(0, 1, 1)）。 */
const TWO_BASE_HUD_STYLE: HudStyle = {
  ...JUDGE_HUD_STYLE,
  background: '#00FFFF',
}

/**
 * ホームランの背景色 + 赤枠（Lua: Color3.new(1, 1, 0.498) と
 * BorderSizePixel = 5 / BorderColor3 = 赤）。
 */
const HOME_RUN_HUD_STYLE: HudStyle = {
  ...JUDGE_HUD_STYLE,
  background: '#FFFF7F',
  border: '#FF0000',
  borderWidth: 5,
}

/**
 * 判定ゾーンの種別。ScoreScript は score.Name（Out/Hit/2Base/HomeRun/
 * ④ホームランゾーン）で、CollisionScript は Collision で分岐する。
 */
type JudgeZoneKind = 'out' | 'hit' | 'twoBase' | 'homeRun' | 'foulOrStrike'

/**
 * ベース名 → 判定ゾーン種別。シーン変換が重複名に付ける「#2」等の接尾辞を
 * 取り除いたベース名で引く（実機の clone は同名のまま増えるのと同じ扱い）。
 */
const ZONE_KIND_BY_BASE_NAME: ReadonlyMap<string, JudgeZoneKind> = new Map([
  ['Out', 'out'],
  ['Hit', 'hit'],
  ['2Base', 'twoBase'],
  ['HomeRun', 'homeRun'],
  ['④ホームランゾーン', 'homeRun'],
  ['Collision', 'foulOrStrike'],
])

/** 打球方向の Z 符号。RJS04 テンプレートはバットから得点板へ向かう -Z。 */
type FieldDirectionZ = -1 | 1

/** シーン変換・クローンの接尾辞（#2 / #cloneN 等)を除いたベース名。 */
const baseName = (name: string): string => {
  const hashIndex = name.indexOf('#')
  if (hashIndex < 0) {
    return name
  }
  return name.slice(0, hashIndex)
}

/** クォータニオン q でベクトル v を回転する（v' = q v q⁻¹）。 */
const rotateByQuaternion = (v: Vec3, q: Quat): Vec3 => {
  // t = 2 * cross(q.xyz, v); v' = v + q.w * t + cross(q.xyz, t)
  const tx = 2 * (q.y * v.z - q.z * v.y)
  const ty = 2 * (q.z * v.x - q.x * v.z)
  const tz = 2 * (q.x * v.y - q.y * v.x)
  return {
    x: v.x + q.w * tx + (q.y * tz - q.z * ty),
    y: v.y + q.w * ty + (q.z * tx - q.x * tz),
    z: v.z + q.w * tz + (q.x * ty - q.y * tx),
  }
}

/** 単位クォータニオンの逆回転（共役）。 */
const conjugate = (q: Quat): Quat => ({ x: -q.x, y: -q.y, z: -q.z, w: q.w })

/**
 * 球（中心 center・半径 radius）が箱パーツ（回転を含む OBB）に接触して
 * いるか。パーツの実行時の向き「Y ヨー(rotationY) ∘ orientation」の逆回転で
 * 球の中心をパーツのローカル座標へ移し、箱の最近接点との距離で判定する
 * （球 vs 箱は厳密、バット等の円柱はその外接箱で近似）。
 */
const sphereTouchesPart = (
  center: Vec3,
  radius: number,
  part: ObjectRuntimeState,
): boolean => {
  const rotation = quatMultiply(
    quatFromAxisAngle('y', part.rotationY),
    part.orientation,
  )
  const local = rotateByQuaternion(
    {
      x: center.x - part.position.x,
      y: center.y - part.position.y,
      z: center.z - part.position.z,
    },
    conjugate(rotation),
  )
  const gapX = Math.max(Math.abs(local.x) - part.size.x / 2, 0)
  const gapY = Math.max(Math.abs(local.y) - part.size.y / 2, 0)
  const gapZ = Math.max(Math.abs(local.z) - part.size.z / 2, 0)
  return gapX * gapX + gapY * gapY + gapZ * gapZ <= radius * radius
}

/**
 * 得点ゾーンがバットのどちら側にあるかから、フィールド側の Z 方向を決める。
 * RJS04 実テンプレートでは ②バット z=-23 付近、得点板 z=-154 付近なので -Z。
 */
const fieldDirectionZFor = (
  bat: ObjectRuntimeState,
  zones: ReadonlyArray<{
    readonly state: ObjectRuntimeState
    readonly kind: JudgeZoneKind
  }>,
): FieldDirectionZ => {
  let zSum = 0
  let count = 0
  for (const zone of zones) {
    if (zone.kind === 'foulOrStrike') {
      continue
    }
    zSum += zone.state.position.z
    count += 1
  }
  if (count === 0) {
    return DEFAULT_FIELD_DIRECTION_Z
  }
  const deltaZ = zSum / count - bat.position.z
  if (Math.abs(deltaZ) <= EPSILON) {
    return DEFAULT_FIELD_DIRECTION_Z
  }
  return deltaZ < 0 ? -1 : 1
}

/**
 * BatScript の「現在速度 5 倍」を土台に、プレビューで欠けているバット反発を
 * 補う。すでに十分な打球方向速度がある場合は Lua の結果をそのまま保つ。
 */
const batHitVelocity = (
  velocity: Vec3,
  fieldDirectionZ: FieldDirectionZ,
): Vec3 => {
  const scaled = {
    x: velocity.x * BAT_HIT_VELOCITY_SCALE,
    y: velocity.y * BAT_HIT_VELOCITY_SCALE,
    z: velocity.z * BAT_HIT_VELOCITY_SCALE,
  }
  const fieldSpeed = scaled.z * fieldDirectionZ
  return {
    x: scaled.x,
    y: Math.max(scaled.y, BAT_HIT_MIN_UPWARD_SPEED),
    z:
      fieldSpeed >= BAT_HIT_MIN_FIELD_SPEED ?
        scaled.z
      : fieldDirectionZ * BAT_HIT_MIN_FIELD_SPEED,
  }
}

/** 判定結果（GUI 文言・効果音・GUI デザイン）。 */
interface JudgeResult {
  readonly text: string
  readonly sfx: SampleSfx
  readonly style: HudStyle
}

/**
 * ゾーン種別 → 判定結果。foulOrStrike だけはバット接触済みかで分岐する
 * （Lua: CollisionScript の hitValue.Value 分岐）。
 */
const judgeResultFor = (
  kind: JudgeZoneKind,
  isBallHit: boolean,
): JudgeResult => {
  switch (kind) {
    case 'out':
      return { text: 'アウト', sfx: FAIL_GAMEOVER_SFX, style: JUDGE_HUD_STYLE }
    case 'hit':
      return { text: 'ヒット！', sfx: TARGET_HIT_SFX, style: JUDGE_HUD_STYLE }
    case 'twoBase':
      return {
        text: 'ツーベース！！',
        sfx: TARGET_HIT_SFX,
        style: TWO_BASE_HUD_STYLE,
      }
    case 'homeRun':
      return {
        text: 'ホームラン！！！',
        sfx: GOAL_SUCCESS_SFX,
        style: HOME_RUN_HUD_STYLE,
      }
    case 'foulOrStrike':
      if (isBallHit) {
        return {
          text: 'ファール',
          sfx: FAIL_GAMEOVER_SFX,
          style: JUDGE_HUD_STYLE,
        }
      }
      return {
        text: 'ストライク',
        sfx: FAIL_GAMEOVER_SFX,
        style: JUDGE_HUD_STYLE,
      }
  }
}

/** RJS04 の環境ビヘイビアを生成する（プレイ開始毎に呼ばれる）。 */
export const createRjs04Behaviors = (): MissionBehaviors => {
  // Lua: ReplicatedStorage.Hit（バット接触済みフラグ）。
  let isBallHit = false
  // Lua: ReplicatedStorage.Judge（判定済みフラグ。プレイ中 1 回だけ判定する）。
  let isJudged = false

  /**
   * ボール監視ループ（BatScript + CollisionScript + ScoreScript +
   * GUILocalScript の統合）。名前が ③ボール のオブジェクト（学習者の
   * control_clone 分も含む）を毎フレーム走査し、バット・判定ゾーンとの
   * 接触を見る。判定が確定したらループを終える（Lua は Judge フラグが
   * 立ったまま戻らないため、以降なにも起きない）。
   */
  const watchBalls: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    while (!ctx.signal.aborted) {
      // 1 パスで名前ごとに仕分けする（ゾーンはシーン順を保つ）。
      const balls: ObjectRuntimeState[] = []
      const bats: ObjectRuntimeState[] = []
      const zones: Array<{
        readonly state: ObjectRuntimeState
        readonly kind: JudgeZoneKind
      }> = []
      for (const state of world.getState().values()) {
        // CanTouch=false は Touched が発火しない（Roblox 準拠）。
        if (!state.canTouch) {
          continue
        }
        const base = baseName(state.name)
        if (base === BALL_NAME) {
          balls.push(state)
          continue
        }
        if (base === BAT_NAME) {
          bats.push(state)
          continue
        }
        const kind = ZONE_KIND_BY_BASE_NAME.get(base)
        if (kind) {
          zones.push({ state, kind })
        }
      }
      for (const ball of balls) {
        // ball 形状の半径は物理コライダーと同じ「最小軸 / 2」
        // （rjf05 の命中判定と同方針）。
        const radius = Math.min(ball.size.x, ball.size.y, ball.size.z) / 2
        // BatScript: バット接触でヒットフラグを立てる（1 回だけ）。あわせて
        // 動的剛体なら Lua の AssemblyLinearVelocity 5 倍を反映し、固定バットの
        // 姿勢更新では乗らない反発速度を打球方向へ補う。
        if (!isBallHit) {
          for (const bat of bats) {
            if (sphereTouchesPart(ball.position, radius, bat)) {
              isBallHit = true
              // 動いている（非 Anchored の）ボールだけ加速する。velocity は再
              // アンカー後も直近の動的値が残り得るため、anchored で明示的に除外する。
              if (ball.velocity && !ball.anchored) {
                world.setObjectVelocity(
                  ball.id,
                  batHitVelocity(ball.velocity, fieldDirectionZFor(bat, zones)),
                )
              }
              break
            }
          }
        }
        // CollisionScript / ScoreScript: 最初に触れたゾーンで判定を確定する。
        if (!isJudged) {
          for (const zone of zones) {
            if (sphereTouchesPart(ball.position, radius, zone.state)) {
              isJudged = true
              const result = judgeResultFor(zone.kind, isBallHit)
              playSample(result.sfx.url, result.sfx.volume, ctx.signal)
              ctx.setHudText?.(result.text, result.style)
              break
            }
          }
        }
      }
      if (isJudged) {
        return
      }
      await ctx.wait(FRAME_SECONDS)
    }
  }

  return { onPlay: [watchBalls] }
}
